import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import {
  Add as AddIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  Edit as EditIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  ListAlt,
  LockOutlined as LockOutlinedIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

import { useAuth } from "../../context/AuthContext";
import { hasPermission } from "../../config/permissionUtils";
import { categoriesService, productsService } from "../../services";
import CustomSnackBar from "../../Components/CustomSnackBar";
import MainText from "../../Components/MainText";

const toArray = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.results)) return value.results;
  return [];
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const yyyy = date.getFullYear();
  const hh = String(date.getHours()).padStart(2, "0");
  const mi = String(date.getMinutes()).padStart(2, "0");
  return `${dd}/${mm}/${yyyy} ${hh}:${mi}`;
};

const formatMoney = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return "-";
  return `Q${parsed.toFixed(2)}`;
};

const formatPercent = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return "-";
  return `${parsed.toFixed(2)}%`;
};

const getPresentationPrice = (presentation) => {
  const prices = toArray(presentation?.prices);
  return prices.find((price) => price?.is_active !== false) || prices[0] || null;
};

export default function ProductsListPage() {
  const isMobile = useMediaQuery("(max-width:768px)");
  const navigate = useNavigate();
  const { isSuperuser, permissions = [] } = useAuth();

  const canViewProducts =
    hasPermission(permissions, "view_products", isSuperuser) ||
    hasPermission(permissions, "manage_products", isSuperuser);
  const canManageProducts = hasPermission(permissions, "manage_products", isSuperuser);
  const canViewProductMargin =
    canManageProducts || hasPermission(permissions, "view_reports", isSuperuser);

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedRows, setExpandedRows] = useState({});

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [activeOnly, setActiveOnly] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState("");

  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMsg, setSnackMsg] = useState("");
  const [snackSev, setSnackSev] = useState("info");

  const showSnack = (message, severity = "info") => {
    setSnackMsg(message);
    setSnackSev(severity);
    setSnackOpen(true);
  };

  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedSearch(search), 450);
    return () => clearTimeout(timeout);
  }, [search]);

  const loadCategories = useCallback(async () => {
    try {
      const data = await categoriesService.list({ active: "true" });
      setCategories(toArray(data));
    } catch {
      setCategories([]);
    }
  }, []);

  const loadProducts = useCallback(async () => {
    if (!canViewProducts) return;

    setLoading(true);
    try {
      const data = await productsService.list({
        search: debouncedSearch || undefined,
        active: activeOnly ? "true" : "false",
        category: categoryFilter || undefined,
      });
      setProducts(toArray(data));
    } catch {
      setProducts([]);
      showSnack("No se pudieron cargar los productos.", "error");
    } finally {
      setLoading(false);
    }
  }, [activeOnly, canViewProducts, categoryFilter, debouncedSearch]);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  const handleToggleActive = useCallback(
    async (row) => {
      try {
        await productsService.toggleActive(row.id, !row.is_active);
        showSnack(
          row.is_active ? "Producto desactivado." : "Producto activado.",
          "success"
        );
        await loadProducts();
      } catch {
        showSnack("No se pudo actualizar el estado del producto.", "error");
      }
    },
    [loadProducts]
  );

  const toggleExpanded = useCallback((productId) => {
    setExpandedRows((prev) => ({
      ...prev,
      [productId]: !prev[productId],
    }));
  }, []);

  const productsWithMeta = useMemo(
    () =>
      products.map((product) => ({
        ...product,
        presentations: toArray(product?.presentations),
      })),
    [products]
  );

  if (!canViewProducts) {
    return (
      <Box sx={{ py: 4 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Sin permiso
        </Typography>
        <Typography variant="body2" color="text.secondary">
          No tienes permisos para ver productos.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%" }}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={1.5}
        alignItems={{ xs: "flex-start", md: "center" }}
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <MainText
          icon={ListAlt}
          title="Listo de productos"
          subtitle="Consulta de productos"
          width="50%"
        />

        <Stack direction="row" spacing={1} alignItems="center">
          <Tooltip title="Refrescar listado">
            <IconButton onClick={loadProducts}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {canManageProducts && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate("/products/create")}>
              Nuevo producto
            </Button>
          )}
        </Stack>
      </Stack>

      <Stack direction={{ xs: "column", md: "row" }} spacing={1} sx={{ mb: 1.5 }}>
        <TextField
          size="small"
          placeholder="Buscar por codigo o descripcion..."
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          sx={{ minWidth: 260 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        <FormControl size="small" sx={{ minWidth: 210 }}>
          <InputLabel>Categoria</InputLabel>
          <Select
            label="Categoria"
            value={categoryFilter}
            onChange={(event) => setCategoryFilter(event.target.value)}
          >
            <MenuItem value="">(Todas)</MenuItem>
            {categories.map((category) => (
              <MenuItem key={category.id} value={category.id}>
                {category.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControlLabel
          control={<Switch checked={activeOnly} onChange={(event) => setActiveOnly(event.target.checked)} />}
          label={activeOnly ? "Activos" : "Inactivos"}
        />
      </Stack>

      <Paper variant="outlined" sx={{ overflow: "hidden" }}>
        <TableContainer sx={{ maxHeight: isMobile ? "calc(100vh - 250px)" : "calc(100vh - 290px)" }}>
          <Table stickyHeader size={isMobile ? "small" : "medium"}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 56 }} />
                <TableCell sx={{ minWidth: 140 }}>Codigo</TableCell>
                <TableCell sx={{ minWidth: 220 }}>Descripcion</TableCell>
                <TableCell sx={{ minWidth: 160 }}>Categoria</TableCell>
                <TableCell sx={{ minWidth: 160 }}>Marca</TableCell>
                <TableCell sx={{ minWidth: 160 }}>Familia</TableCell>
                <TableCell sx={{ minWidth: 220 }}>Presentaciones</TableCell>
                <TableCell sx={{ minWidth: 110 }}>Estado</TableCell>
                <TableCell sx={{ minWidth: 160 }}>Creado</TableCell>
                <TableCell align="right" sx={{ minWidth: 120 }}>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!loading && productsWithMeta.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10}>
                    <Typography variant="body2" color="text.secondary">
                      No se encontraron productos con los filtros actuales.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : null}

              {productsWithMeta.map((row) => {
                const isExpanded = Boolean(expandedRows[row.id]);
                const hasPresentations = row.presentations.length > 0;

                return (
                  <React.Fragment key={row.id}>
                    <TableRow hover>
                      <TableCell>
                        <Tooltip title={isExpanded ? "Ocultar presentaciones" : "Ver presentaciones"}>
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => toggleExpanded(row.id)}
                              disabled={!hasPresentations}
                            >
                              {isExpanded ? (
                                <ExpandLessIcon fontSize="small" />
                              ) : (
                                <ExpandMoreIcon fontSize="small" />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>{row.code || "-"}</TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {row.description || "-"}
                        </Typography>
                      </TableCell>
                      <TableCell>{row.category?.name || "-"}</TableCell>
                      <TableCell>{row.brand?.name || "-"}</TableCell>
                      <TableCell>{row.family_product?.name || "-"}</TableCell>
                      <TableCell>
                        {hasPresentations ? (
                          <Stack direction="row" spacing={0.6} useFlexGap flexWrap="wrap">
                            {row.presentations.map((presentation) => (
                              <Chip
                                key={`presentation-pill-${row.id}-${presentation.id}`}
                                size="small"
                                variant="outlined"
                                label={presentation?.product_format?.name || "Presentacion"}
                              />
                            ))}
                          </Stack>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            -
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={row.is_active ? "Activo" : "Inactivo"}
                          color={row.is_active ? "success" : "default"}
                          variant={row.is_active ? "filled" : "outlined"}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary" sx={{ fontStyle: "italic" }}>
                          {formatDateTime(row.created_at)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.25} justifyContent="flex-end">
                          <Tooltip title="Editar">
                            <span>
                              <IconButton
                                size="small"
                                disabled={!canManageProducts}
                                onClick={() => navigate(`/products/create?product_id=${row.id}`)}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title={row.is_active ? "Desactivar" : "Activar"}>
                            <span>
                              <IconButton
                                size="small"
                                disabled={!canManageProducts}
                                color={row.is_active ? "error" : "success"}
                                onClick={() => handleToggleActive(row)}
                              >
                                {row.is_active ? (
                                  <BlockIcon fontSize="small" />
                                ) : (
                                  <CheckCircleIcon fontSize="small" />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>

                    <TableRow>
                      <TableCell colSpan={10} sx={{ py: 0, borderBottom: isExpanded ? undefined : 0 }}>
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <Box sx={{ px: { xs: 1, md: 2 }, py: 2, bgcolor: "action.hover" }}>
                            {hasPresentations ? (
                              <Box
                                sx={{
                                  display: "grid",
                                  gridTemplateColumns: {
                                    xs: "1fr",
                                    md: "repeat(2, minmax(0, 1fr))",
                                    xl: "repeat(3, minmax(0, 1fr))",
                                  },
                                  gap: 1.5,
                                }}
                              >
                                {row.presentations.map((presentation) => {
                                  const activePrice = getPresentationPrice(presentation);
                                  const isLocked = Boolean(
                                    presentation?.has_active_stock || presentation?.has_transactions
                                  );

                                  return (
                                    <Card key={`presentation-card-${row.id}-${presentation.id}`} variant="outlined">
                                      <CardContent sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
                                        <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="flex-start">
                                          <Box sx={{ minWidth: 0 }}>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                              {presentation?.product_format?.name || "Presentacion"}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                              Equivalencia base
                                            </Typography>
                                          </Box>
                                          {isLocked ? (
                                            <Tooltip title="Presentacion bloqueada por stock activo o transacciones.">
                                              <Chip
                                                size="small"
                                                color="warning"
                                                icon={<LockOutlinedIcon fontSize="small" />}
                                                label="Candado"
                                              />
                                            </Tooltip>
                                          ) : null}
                                        </Stack>

                                        <Box
                                          sx={{
                                            display: "grid",
                                            gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                                            gap: 1,
                                          }}
                                        >
                                          <Box>
                                            <Typography variant="caption" color="text.secondary">
                                              Cantidad base
                                            </Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                              {presentation?.qty_in_base || "-"}
                                            </Typography>
                                          </Box>
                                          {canManageProducts ? (
                                            <Box>
                                              <Typography variant="caption" color="text.secondary">
                                                Costo
                                              </Typography>
                                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                {formatMoney(presentation?.cost_price)}
                                              </Typography>
                                            </Box>
                                          ) : null}
                                          <Box>
                                            <Typography variant="caption" color="text.secondary">
                                              Precio de venta
                                            </Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                              {formatMoney(activePrice?.sale_price)}
                                            </Typography>
                                          </Box>
                                          {canViewProductMargin ? (
                                            <Box>
                                              <Typography variant="caption" color="text.secondary">
                                                Margen
                                              </Typography>
                                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                {formatPercent(activePrice?.utility_pct)}
                                              </Typography>
                                            </Box>
                                          ) : null}
                                        </Box>
                                      </CardContent>
                                    </Card>
                                  );
                                })}
                              </Box>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                Este producto no tiene presentaciones activas.
                              </Typography>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })}

              {loading ? (
                <TableRow>
                  <TableCell colSpan={10}>
                    <Typography variant="body2" color="text.secondary">
                      Cargando productos...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <CustomSnackBar
        open={snackOpen}
        setOpen={setSnackOpen}
        message={snackMsg}
        severity={snackSev}
      />
    </Box>
  );
}
